import {
  Box,
  Container,
  Divider,
  Paper,
  Typography,
  useTheme
} from '@mui/material'

import { ClientDataChart } from '../charts'
import { DashboardCounter } from './dashboardCounter'
import { DateRangeSlider } from './dateRangeSlider'
import { parseGraphMonthLabel } from '../../utils'
import { constants } from '../../config'

const { catOptions, catNames } = constants

// Main chart region: date tiles, counter, range slider, line chart
const DashboardChartPaper = ({ props: viewProps = {}, controls = {} }) => {
  const {
    dateRangeValue,
    projectGraphLabels,
    activeTab,
    graphStatistics,
    billableSelected,
    graphData,
    conjoinedOptions,
    projectOptions
  } = viewProps

  const {
    handleSliderChange,
    handleLabelFormat
  } = controls

  const theme = useTheme()
  const statTileSx = {
    px: 1.5,
    py: 1,
    borderRadius: 1,
    ...theme.custom.colors.dashboard.statTile,
    minHeight: 56
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 64px)',
        overflowX: 'hidden',
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.default
      }}
    >
      <Container maxWidth='xl' disableGutters sx={{ px: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.custom.colors.dashboard.chartPaperShadow
          }}
        >
          <Box sx={{ p: { xs: 1.5, md: 2 }, pb: '0 !important' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 1.5, md: 2 },
                mb: 2
              }}
            >
              <Box
                sx={{
                  minWidth: { xs: '100%', md: 140 },
                  ...statTileSx
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography
                    variant='overline'
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.1,
                      letterSpacing: 0.6,
                      fontSize: '0.7rem'
                    }}
                  >
                    Start Date
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 900,
                      color: theme.palette.text.primary,
                      lineHeight: 1.1,
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '1.1rem'
                    }}
                  >
                    {(() => {
                      const startIdx = Array.isArray(dateRangeValue) ? dateRangeValue[0] : 0
                      const startDateStr = projectGraphLabels?.[startIdx]
                      if (!startDateStr) return 'N/A'
                      const baseDate = parseGraphMonthLabel(startDateStr)
                      if (!baseDate || Number.isNaN(baseDate.getTime())) return 'N/A'
                      const dateObj = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
                      return dateObj.toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric'
                      })
                    })()}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1.5, md: 2 },
                  minWidth: 0
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, flex: 1 }}>
                  <DashboardCounter
                    activeTab={activeTab}
                    catOptions={catOptions}
                    catNames={catNames}
                    graphStatistics={graphStatistics}
                    billableSelected={billableSelected}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  minWidth: { xs: '100%', md: 140 },
                  ...statTileSx
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography
                    variant='overline'
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.1,
                      letterSpacing: 0.6,
                      fontSize: '0.7rem'
                    }}
                  >
                    End Date
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 900,
                      color: theme.palette.text.primary,
                      lineHeight: 1.1,
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '1.1rem'
                    }}
                  >
                    {(() => {
                      const endIdx = Array.isArray(dateRangeValue) ? dateRangeValue[1] : (projectGraphLabels?.length || 1) - 1
                      const endDateStr = projectGraphLabels?.[endIdx]
                      if (!endDateStr) return 'N/A'
                      const baseDate = parseGraphMonthLabel(endDateStr)
                      if (!baseDate || Number.isNaN(baseDate.getTime())) return 'N/A'
                      const dateObj = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
                      return dateObj.toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric'
                      })
                    })()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                // Inset for thumbs/hover — on wrapper, not Slider, so ticks line up with the rail
                px: { xs: theme.spacing(2), sm: theme.spacing(2.25) },
                pb: { xs: 1, md: 1.5 }
              }}
            >
              <DateRangeSlider
                value={dateRangeValue}
                max={Math.max(0, projectGraphLabels.length - 1)}
                onChange={handleSliderChange}
                formatLabel={handleLabelFormat}
              />
            </Box>
          </Box>

          <Divider />

          <Box sx={{ p: { xs: 1.5, md: 2 }, minHeight: '60vh', position: 'relative' }}>
            {
              Array.isArray(projectOptions) && projectOptions.length > 0 &&
              graphData?.[activeTab] && conjoinedOptions?.[activeTab]?.graphInfo && (
                <ClientDataChart
                  props={{
                    clientData: graphData[activeTab] || {},
                    graphOptions: conjoinedOptions[activeTab].graphInfo,
                    activeTab
                  }}
                />
              )
            }
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export { DashboardChartPaper }
